import { useToast } from '@chakra-ui/react';
import {
  useCreateNotificationTemplateMutation,
  useDeleteNotificationTemplateMutation,
  usePublishNotificationTemplatesMutation,
  useUpdateNotificationTemplateMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  CreateNotificationTemplateInput,
  UpdateNotificationTemplateInput,
} from '@client/graphql/__generated__/types';

export const useNotificationTemplateMutations = () => {
  const toast = useToast();

  const [createMutation, { loading: creating }] =
    useCreateNotificationTemplateMutation();
  const [updateMutation, { loading: updating }] =
    useUpdateNotificationTemplateMutation();
  const [deleteMutation, { loading: deleting }] =
    useDeleteNotificationTemplateMutation();
  const [publishMutation, { loading: publishing }] =
    usePublishNotificationTemplatesMutation();

  const createTemplate = async (input: CreateNotificationTemplateInput) => {
    try {
      const result = await createMutation({
        variables: { input },
        refetchQueries: ['NotificationTemplatesList'],
      });
      toast({
        title: 'Notification created',
        status: 'success',
        duration: 3000,
      });

      return result.data?.createNotificationTemplate;
    } catch (error) {
      toast({
        title: 'Failed to create notification',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const updateTemplate = async (input: UpdateNotificationTemplateInput) => {
    try {
      const result = await updateMutation({
        variables: { input },
        refetchQueries: ['NotificationTemplatesList'],
      });
      toast({
        title: 'Notification updated',
        status: 'success',
        duration: 3000,
      });

      return result.data?.updateNotificationTemplate;
    } catch (error) {
      toast({
        title: 'Failed to update notification',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      await deleteMutation({
        variables: { id },
        refetchQueries: ['NotificationTemplatesList'],
      });
      toast({
        title: 'Notification deleted',
        status: 'success',
        duration: 3000,
      });

      return true;
    } catch (error) {
      toast({
        title: 'Failed to delete notification',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const publishTemplates = async (partnerId: string) => {
    try {
      const result = await publishMutation({
        variables: { partnerId },
        refetchQueries: ['NotificationTemplatesList'],
      });
      toast({
        title: 'Templates published',
        status: 'success',
        duration: 3000,
      });

      return result.data?.publishNotificationTemplates;
    } catch (error) {
      toast({
        title: 'Failed to publish templates',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  return {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    publishTemplates,
    loading: creating || updating || deleting || publishing,
    creating,
    updating,
    deleting,
    publishing,
  };
};
